// The CUR usage-type map (SCR-012, API-035): which CUR usage type is which Bedrock model and token
// class. Global, not per customer; a mapping's row opens its summary with Delete, confirmed by the
// typed pattern. Adding a verified mapping (or deleting one) needs `billing:approve`.
import { useEffect, useState } from "preact/hooks";
import type { ApiClient } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { ConfirmDialog } from "@nexus/ui";
import { Checkbox, Field, hintId } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { ErrorBanner } from "@nexus/ui";
import { RowDetail } from "@nexus/ui";
import { DataTable, type Column } from "@nexus/ui";
import {
  TOKEN_CLASSES,
  TOKEN_CLASS_LABELS,
  canRequestVerified,
  usageTypeRows,
  validateNewUsageType,
  type NewUsageTypeInput,
  type TokenClass,
  type UsageTypeRowView,
} from "./model";

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  throw error;
}

const EMPTY_INPUT: NewUsageTypeInput = { productCode: "AmazonBedrock", usageTypePattern: "", bedrockModelId: "", tokenClass: "input", source: "", verified: false };

export function UsageTypesSection({ client, canApprove }: { client: ApiClient; canApprove: boolean }) {
  const [rows, setRows] = useState<UsageTypeRowView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [input, setInput] = useState<NewUsageTypeInput>(EMPTY_INPUT);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<UsageTypeRowView | null>(null);
  const [deleting, setDeleting] = useState<UsageTypeRowView | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/admin/cur-usage-types")
      .then((page) => setRows(usageTypeRows(page.items)))
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const fieldErrors = validateNewUsageType(input);
  const canSubmit = Object.keys(fieldErrors).length === 0 && canRequestVerified(canApprove, input.verified);

  const addMapping = (event: Event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    client
      .post("/v1/admin/cur-usage-types", { body: { ...input, tokenClass: input.tokenClass as TokenClass } })
      .then(() => {
        setInput(EMPTY_INPUT);
        load();
      })
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setBusy(false));
  };

  // A refused delete reopens the mapping's summary, which shows why.
  const removeMapping = (row: UsageTypeRowView) => {
    setBusy(true);
    client
      .delete("/v1/admin/cur-usage-types", { query: { productCode: row.productCode, usageTypePattern: row.usageTypePattern } })
      .then(load)
      .catch((err: unknown) => (setError(asApiError(err)), setSelected(row)))
      .finally(() => (setBusy(false), setDeleting(null)));
  };

  const statusCell = (row: UsageTypeRowView) =>
    row.verified ? (
      <>
        <Chip tone="green">Verified</Chip>
        {row.reviewedOn && <span class="cell-sub">{row.reviewedOn}</span>}
      </>
    ) : (
      <Chip tone="amber">Unverified</Chip>
    );
  const deleteRefused = (row: UsageTypeRowView) => row.verified && !canApprove;

  const columns: Column<UsageTypeRowView>[] = [
    {
      key: "usageTypePattern",
      header: "Usage type pattern",
      help: "finance.usageTypePattern",
      cell: (row) => (
        <span class="name-with-id">
          <span class="mono">{row.usageTypePattern}</span>
          <span class="cell-sub">{row.productCode}</span>
        </span>
      ),
      compare: (a, b) => a.usageTypePattern.localeCompare(b.usageTypePattern),
    },
    { key: "bedrockModelId", header: "Model", cell: (row) => <span class="mono">{row.bedrockModelId}</span>, compare: (a, b) => a.bedrockModelId.localeCompare(b.bedrockModelId) },
    { key: "tokenClass", header: "Token class", help: "finance.tokenClass", cell: (row) => TOKEN_CLASS_LABELS[row.tokenClass] },
    {
      key: "verified",
      header: "Status",
      help: "finance.markVerified",
      cell: statusCell,
    },
    { key: "source", header: "Source", help: "finance.mappingSource", cell: (row) => row.source },
  ];

  return (
    <Card title="CUR usage-type map" help="finance.usageTypes">
      <p class="card-note">Which CUR usage type is which Bedrock model and token class, for every customer. Only verified mappings are used.</p>
      <DataTable
        caption="CUR usage types mapped to a Bedrock model and token class"
        hideCaption
        columns={columns}
        rows={rows}
        rowKey={(row) => `${row.productCode}\u0000${row.usageTypePattern}`}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage="No mappings yet. Add one below so the actual AWS bill can be matched to metered tokens."
        onRowClick={setSelected}
        rowLabel={(row) => `Mapping ${row.usageTypePattern}`}
      />
      {selected && (
        <RowDetail
          title={`Mapping ${selected.usageTypePattern}`}
          open
          onClose={() => setSelected(null)}
          facts={[
            { label: "Product code", value: <span class="mono">{selected.productCode}</span> },
            { label: "Model", value: <span class="mono">{selected.bedrockModelId}</span> },
            { label: "Token class", value: TOKEN_CLASS_LABELS[selected.tokenClass] },
            { label: "Status", value: statusCell(selected) },
            { label: "Source", value: selected.source },
          ]}
          actions={
            <button type="button" class="button button-danger" disabled={busy || deleteRefused(selected)} onClick={() => (setSelected(null), setDeleting(selected))}>
              Delete
            </button>
          }
        >
          {deleteRefused(selected) && <p class="card-note">Deleting a verified mapping needs billing:approve.</p>}
          {error && <ErrorBanner error={error} />}
        </RowDetail>
      )}
      <ConfirmDialog
        title="Delete mapping"
        open={deleting !== null}
        message={deleting ? `Deleting ${deleting.usageTypePattern} unmaps it from ${deleting.bedrockModelId}: CUR lines it matched are no longer matched to metered tokens.` : ""}
        confirmLabel="Delete"
        typedName={deleting?.usageTypePattern ?? ""}
        busy={busy}
        onConfirm={() => deleting && removeMapping(deleting)}
        onClose={() => setDeleting(null)}
      />
      <form class="stack-sm" onSubmit={addMapping}>
        <h3>Add a mapping</h3>
        <div class="form-grid">
          <Field id="ut-product" label="Product code" help="finance.productCode">
            <input id="ut-product" class="input input-mono" value={input.productCode} onInput={(e) => setInput({ ...input, productCode: e.currentTarget.value })} />
          </Field>
          <Field id="ut-pattern" label="Usage type pattern" help="finance.usageTypePattern">
            <input
              id="ut-pattern"
              class="input input-mono"
              placeholder="*-claude-sonnet-5-input-tokens"
              value={input.usageTypePattern}
              onInput={(e) => setInput({ ...input, usageTypePattern: e.currentTarget.value })}
            />
          </Field>
          <Field id="ut-model" label="Bedrock model id" help="finance.bedrockModelId">
            <input id="ut-model" class="input input-mono" value={input.bedrockModelId} onInput={(e) => setInput({ ...input, bedrockModelId: e.currentTarget.value })} />
          </Field>
          <Field id="ut-class" label="Token class" help="finance.tokenClass">
            <select id="ut-class" class="input" value={input.tokenClass} onChange={(e) => setInput({ ...input, tokenClass: e.currentTarget.value })}>
              {TOKEN_CLASSES.map((tokenClass) => (
                <option key={tokenClass} value={tokenClass}>
                  {TOKEN_CLASS_LABELS[tokenClass]}
                </option>
              ))}
            </select>
          </Field>
          <Field id="ut-source" label="Source" help="finance.mappingSource">
            <input id="ut-source" class="input" placeholder="AWS Bedrock pricing page" value={input.source} onInput={(e) => setInput({ ...input, source: e.currentTarget.value })} />
          </Field>
        </div>
        <Checkbox
          id="ut-verified"
          label="Mark verified"
          help="finance.markVerified"
          checked={input.verified}
          disabled={!canApprove}
          describedBy={canApprove ? undefined : hintId("ut-verified")}
          onChange={(verified) => setInput({ ...input, verified })}
        />
        {!canApprove && (
          <span class="field-hint" id={hintId("ut-verified")}>
            Needs the billing:approve permission.
          </span>
        )}
        <div class="form-actions">
          <button type="submit" class="button button-primary" disabled={!canSubmit || busy}>
            Add mapping
          </button>
        </div>
      </form>
    </Card>
  );
}
